import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  LayoutDashboard, FileText, CalendarDays, Save, Link2, Target,
  Gift, BarChart3, HelpCircle, Settings, Shield, LogOut, Menu, Video, Briefcase, ChevronDown, Search
} from "lucide-react";
import { LOGO_URL, API, BACKEND_URL } from "@/config";
import { useAuth } from "@/components/AuthProvider";
import { WhatsNew } from "@/components/WhatsNew";

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWsId, setActiveWsId] = useState(null);
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const [personalBrand, setPersonalBrand] = useState({});

  useEffect(() => {
    if (user?.plan !== "enterprise") return;
    axios.get(`${API}/workspaces`).then((r) => {
      setWorkspaces(r.data.workspaces || []);
      setActiveWsId(r.data.active_workspace_id || null);
    }).catch(() => {});
  }, [user?.id, user?.plan]);

  // Pull the user's personal-account branding (Settings → White-Label).
  // Used as a smart fallback when an active workspace has no per-client logo/color set.
  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/branding`).then((r) => {
      setPersonalBrand(r.data || {});
    }).catch(() => {});
  }, [user?.id]);

  const switchWorkspace = async (id) => {
    try {
      if (id) {
        await axios.post(`${API}/workspaces/switch/${id}`);
      } else {
        await axios.post(`${API}/workspaces/switch-personal`);
      }
      setActiveWsId(id);
      setWsMenuOpen(false);
      toast.success(id ? "Workspace switched" : "Switched to Personal");
      // Full reload — every page caches its own scoped data (content, templates,
      // affiliates, leads, etc.), so the only reliable way to refresh everything
      // is to reload. Linear/Notion/Slack all do this on workspace switch.
      setTimeout(() => window.location.reload(), 300);
    } catch { toast.error("Could not switch"); }
  };

  const activeWs = workspaces.find((w) => w.id === activeWsId);
  const wsBrand = activeWs?.branding || {};
  // Smart fallback chain: workspace logo → personal account logo → Creatara default.
  // Same chain for brand_name (workspace > workspace.name > personal > "Creatara")
  // and primary_color (workspace > personal > red default).
  const personalLogoUrl = personalBrand.logo_url ? `${BACKEND_URL}${personalBrand.logo_url}` : null;
  const wsLogoUrl = wsBrand.logo_url ? `${BACKEND_URL}${wsBrand.logo_url}` : null;
  const wsLogo = wsLogoUrl || (activeWs ? personalLogoUrl : null);
  const wsName = wsBrand.brand_name || activeWs?.name;
  const wsColor = wsBrand.primary_color || (activeWs ? personalBrand.primary_color : null);
  // Personal-mode chrome (no workspace active): still respect the user's own personal branding.
  const headerLogo = activeWs ? (wsLogo || LOGO_URL) : (personalLogoUrl || LOGO_URL);
  const headerName = activeWs ? wsName : (personalBrand.brand_name || "Creatara");

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    { icon: FileText, label: "Content", path: "/dashboard/content" },
    { icon: Target, label: "Campaigns", path: "/dashboard/campaigns" },
    { icon: Video, label: "Videos", path: "/dashboard/videos" },
    { icon: CalendarDays, label: "Calendar", path: "/dashboard/calendar" },
    { icon: FileText, label: "Flyers & Menus", path: "/dashboard/flyers", isNew: true },
    { icon: Save, label: "Templates", path: "/dashboard/templates" },
    { icon: Link2, label: "Affiliates", path: "/dashboard/affiliates" },
    { icon: Target, label: "Leads", path: "/dashboard/leads" },
    { icon: Gift, label: "Referrals", path: "/dashboard/referrals" },
    { icon: BarChart3, label: "Analytics", path: "/dashboard/analytics" },
    { icon: Search, label: "AI Citations", path: "/dashboard/citations" },
    ...(user?.plan === "enterprise" ? [{ icon: Briefcase, label: "Workspaces", path: "/dashboard/workspaces" }] : []),
    { icon: HelpCircle, label: "Help", path: "/dashboard/help" },
    { icon: Settings, label: "Settings", path: "/dashboard/settings" },
    ...(user?.is_admin ? [{ icon: Shield, label: "Admin Panel", path: "/admin", isAdmin: true }] : [])
  ];

  return (
    <div className="min-h-screen bg-background flex">
      <AnimatePresence>
        {sidebarOpen && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />}
      </AnimatePresence>
      <aside className={`fixed md:sticky md:top-0 md:self-start inset-y-0 left-0 z-50 w-64 h-screen bg-card border-r border-border transform transition-transform duration-200 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        {/* Brand-color accent strip — only when a custom color is actually set (workspace OR personal) */}
        {wsColor && (
          <div className="h-1 w-full flex-shrink-0" style={{ backgroundColor: wsColor }} data-testid="workspace-accent-strip" />
        )}
        <div className="p-6 pb-3 flex-shrink-0">
          <Link to="/" className="flex items-center gap-3 mb-4" data-testid="sidebar-logo-link">
            <img
              src={headerLogo}
              alt={`${headerName} - Dashboard`}
              className="w-9 h-9 rounded-lg object-cover"
              onError={(e) => { e.currentTarget.src = LOGO_URL; }}
              onLoad={(e) => { if (e.currentTarget.naturalWidth <= 2 && !e.currentTarget.src.endsWith(LOGO_URL)) e.currentTarget.src = LOGO_URL; }}
              data-testid="sidebar-logo-img"
            />
            <span className="text-xl font-bold truncate" data-testid="sidebar-brand-name">{headerName}</span>
          </Link>
          {/* Workspace switcher (Enterprise + has at least one client workspace) */}
          {user?.plan === "enterprise" && workspaces.length > 0 && (
            <div className="relative" data-testid="workspace-switcher">
              <button onClick={() => setWsMenuOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left text-sm" data-testid="workspace-switcher-btn">
                <span className="truncate">{activeWs ? activeWs.branding?.brand_name || activeWs.name : "Personal"}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${wsMenuOpen ? "rotate-180" : ""}`} />
              </button>
              {wsMenuOpen && (
                <div className="absolute left-0 right-0 mt-1 z-30 bg-card border border-white/10 rounded-lg overflow-hidden shadow-xl" data-testid="workspace-switcher-menu">
                  <button onClick={() => switchWorkspace(null)} className={`w-full text-left px-3 py-2 text-sm hover:bg-white/10 ${!activeWsId ? "bg-white/5" : ""}`} data-testid="workspace-option-personal">
                    Personal
                    <span className="block text-xs text-muted-foreground">{personalBrand?.brand_name ? `Your branding: ${personalBrand.brand_name}` : "Your own account · /dashboard"}</span>
                  </button>
                  {workspaces.map((ws) => (
                    <button key={ws.id} onClick={() => switchWorkspace(ws.id)} className={`w-full text-left px-3 py-2 text-sm hover:bg-white/10 ${activeWsId === ws.id ? "bg-white/5" : ""}`} data-testid={`workspace-option-${ws.slug}`}>
                      {ws.branding?.brand_name || ws.name}
                      <span className="block text-xs text-muted-foreground">/w/{ws.slug}</span>
                    </button>
                  ))}
                  <Link to="/dashboard/workspaces" onClick={() => setWsMenuOpen(false)} className="block px-3 py-2 text-sm text-violet-400 hover:bg-white/10 border-t border-white/10" data-testid="workspace-option-manage">Manage workspaces →</Link>
                </div>
              )}
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto px-4 space-y-0.5 min-h-0">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`} onClick={() => setSidebarOpen(false)} data-testid={`nav-${item.label.toLowerCase()}`}>
              <item.icon className="w-5 h-5" />{item.label}
            </Link>
          ))}
        </nav>
        <div className="flex-shrink-0 p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={wsColor
                ? { backgroundColor: `${wsColor}33`, color: wsColor }
                : { backgroundColor: "rgba(124,92,252,0.2)", color: "rgb(239,68,68)" }}
              data-testid="user-avatar"
            >
              <span className="font-semibold">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0"><p className="font-medium truncate text-sm">{user?.name}</p><Badge variant="secondary" className="text-xs">{user?.plan?.toUpperCase()}</Badge></div>
          </div>
          <Button variant="ghost" className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground" onClick={() => { logout(); navigate("/"); toast.success("Logged out"); }} data-testid="logout-btn"><LogOut className="w-4 h-4" /> Sign Out</Button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="md:hidden glass border-b border-border p-4 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-6 h-6" /></Button>
          <img
            src={headerLogo}
            alt={`${headerName} - Mobile`}
            className="w-8 h-8 rounded-lg object-cover bg-white"
            onError={(e) => { e.currentTarget.src = LOGO_URL; }}
          />
          <div className="w-10" />
        </header>
        <main className="flex-1 p-6 md:p-8 overflow-auto">{children}</main>
      </div>
      <WhatsNew />
    </div>
  );
};
